import React, { useRef, useEffect, useCallback } from 'react';

/**
 * MobileBarcodeCaptureInput - Invisible input field for enterprise mobile scanners
 *
 * Problem: Zebra and other enterprise Android scanners send barcode data as keyboard input,
 * but Android WebView/Chrome requires an input field to be focused to receive this data.
 * The global keydown listener (useSimpleBarcodeScanner) doesn't work on these devices.
 *
 * Solution: This component creates an invisible input field that:
 * 1. Automatically maintains focus when enabled
 * 2. Captures scanner input and forwards it to the onScan callback
 * 3. Works alongside useSimpleBarcodeScanner without interference
 *
 * The barcode is passed directly to onScan - all parsing and business logic
 * should be handled by the parent component (same as useSimpleBarcodeScanner).
 *
 * Usage:
 * <MobileBarcodeCaptureInput
 *   enabled={!isAnyModalOpen}
 *   onScan={(barcode) => processGlobalBarcode(barcode)}
 * />
 */

export interface MobileBarcodeCaptureInputProps {
  /**
   * Callback when a barcode is scanned (Enter pressed or timeout)
   * The raw barcode string is passed - handle parsing in parent
   */
  onScan: (barcode: string) => void;

  /**
   * Whether the capture input is enabled
   * When false, the input won't render or try to maintain focus
   * Default: true
   */
  enabled?: boolean;

  /**
   * Timeout in ms before auto-processing input (for scanners that don't send Enter)
   * Default: 150
   */
  scanTimeout?: number;

  /**
   * Delay in ms before re-focusing after blur
   * Default: 100
   */
  refocusDelay?: number;
}

export const MobileBarcodeCaptureInput: React.FC<MobileBarcodeCaptureInputProps> = ({
  onScan,
  enabled = true,
  scanTimeout = 150,
  refocusDelay = 100
}) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  const isMouseDownRef = useRef<boolean>(false);

  // Focus the input
  const focusInput = useCallback(() => {
    if (inputRef.current && enabled) {
      inputRef.current.focus();
    }
  }, [enabled]);

  // Check if user is selecting text (mouse held down or text already selected)
  const isUserSelectingText = useCallback(() => {
    if (isMouseDownRef.current) return true;
    const selection = window.getSelection();
    return selection && selection.toString().length > 0;
  }, []);

  // Re-focus when focus is lost (unless user tapped another input or is selecting text)
  const handleBlur = useCallback((e: React.FocusEvent<HTMLInputElement>) => {
    if (!enabled) return;

    // Check if focus moved to another input/textarea/select
    const relatedTarget = e.relatedTarget as HTMLElement | null;
    const isOtherInput = relatedTarget && (
      relatedTarget.tagName === 'INPUT' ||
      relatedTarget.tagName === 'TEXTAREA' ||
      relatedTarget.tagName === 'SELECT' ||
      relatedTarget.isContentEditable
    );

    // Don't refocus if user intentionally clicked another input
    if (isOtherInput) {
      return;
    }

    // Don't refocus if user is selecting text
    if (isUserSelectingText()) {
      return;
    }

    // Re-focus after a short delay
    setTimeout(focusInput, refocusDelay);
  }, [enabled, focusInput, refocusDelay, isUserSelectingText]);

  // Handle input changes
  const handleInput = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    // Clear existing timeout
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    // Set timeout to process barcode if no more input comes
    timeoutRef.current = setTimeout(() => {
      if (inputRef.current && inputRef.current.value.trim()) {
        const barcode = inputRef.current.value.trim();
        console.log('📱 MobileBarcodeCaptureInput: Scan detected:', barcode);
        onScan(barcode);
        inputRef.current.value = '';
      }
    }, scanTimeout);
  }, [onScan, scanTimeout]);

  // Handle Enter key (immediate processing)
  const handleKeyDown = useCallback((e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();

      // Clear timeout
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }

      // Process immediately
      if (inputRef.current && inputRef.current.value.trim()) {
        const barcode = inputRef.current.value.trim();
        console.log('📱 MobileBarcodeCaptureInput: Scan detected (Enter):', barcode);
        onScan(barcode);
        inputRef.current.value = '';
      }
    }
  }, [onScan]);

  // Initial focus and re-focus on enable
  useEffect(() => {
    if (enabled) {
      // Small delay to ensure DOM is ready
      setTimeout(focusInput, 50);
    }
  }, [enabled, focusInput]);

  // Track mouse down/up to detect text selection in progress
  useEffect(() => {
    if (!enabled) return;

    const handleMouseDown = () => { isMouseDownRef.current = true; };
    const handleMouseUp = () => {
      isMouseDownRef.current = false;
      // After mouse up, check if text was selected - if so, don't refocus
      const selection = window.getSelection();
      if (selection && selection.toString().length > 0) {
        return;
      }
      // No text selected after mouse up on non-input area: safe to refocus
      const activeEl = document.activeElement as HTMLElement | null;
      const isOnInput = activeEl && (
        activeEl.tagName === 'INPUT' ||
        activeEl.tagName === 'TEXTAREA' ||
        activeEl.tagName === 'SELECT' ||
        activeEl.isContentEditable
      );
      if (!isOnInput) {
        setTimeout(focusInput, 50);
      }
    };

    document.addEventListener('mousedown', handleMouseDown);
    document.addEventListener('mouseup', handleMouseUp);
    return () => {
      document.removeEventListener('mousedown', handleMouseDown);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [enabled, focusInput]);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  if (!enabled) {
    return null;
  }

  return (
    <input
      ref={inputRef}
      type="text"
      autoComplete="off"
      autoCapitalize="off"
      autoCorrect="off"
      spellCheck={false}
      inputMode="none" // Prevents virtual keyboard on touch
      aria-hidden="true"
      tabIndex={-1}
      onBlur={handleBlur}
      onChange={handleInput}
      onKeyDown={handleKeyDown}
      style={{
        position: 'fixed',
        top: -100,
        left: -100,
        width: 1,
        height: 1,
        opacity: 0,
        pointerEvents: 'none',
        zIndex: -1
      }}
    />
  );
};

export default MobileBarcodeCaptureInput;
